import { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';

import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import { GooglePlacesAutocomplete } from 'event';
import { CategorySelect } from 'profile';
import { type ProfileCategory, type ProfileSkill } from 'profile/profile.api';
import { SkillsAutocomplete } from 'profile/skills-autocomplete';
import { normalizeEndDateTimeForStartChange } from 'lib/datetime-local';
import {
  createPositionRequest,
  fetchAccountPositions,
  fetchPositions,
  readCachedAccountPositions,
  readCachedPositions,
  type PositionBudgetCurrency,
  type PositionSummary,
} from 'position';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import Footer from 'examples/Footer';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from 'currency/currency';

interface PositionRow {
  title: string;
  detailsPath: string;
  status: string;
  budget: string;
  schedule: string;
  skills: string;
}

function formatCurrency(value: number, currency: PositionBudgetCurrency): string {
  if (!Number.isFinite(value)) {
    return '0.00';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function formatBudget(position: PositionSummary): string {
  const hasMin = typeof position.budgetMin === 'number';
  const hasMax = typeof position.budgetMax === 'number';

  if (!hasMin && !hasMax) {
    return 'No budget';
  }

  const currency = position.budgetCurrency ?? DEFAULT_CURRENCY_CODE;
  const minLabel = hasMin ? formatCurrency(position.budgetMin as number, currency) : null;
  const maxLabel = hasMax ? formatCurrency(position.budgetMax as number, currency) : null;

  if (minLabel && maxLabel) {
    return `${minLabel} - ${maxLabel}`;
  }

  if (minLabel) {
    return `From ${minLabel}`;
  }

  return maxLabel ? `Up to ${maxLabel}` : 'No budget';
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toISOString();
  }
}

function formatSchedule(position: PositionSummary): string {
  const startLabel = formatDateTime(position.startsAt ?? undefined);
  const endLabel = formatDateTime(position.endsAt ?? undefined);

  if (!startLabel && !endLabel) {
    return 'Flexible';
  }

  if (startLabel && endLabel) {
    return `${startLabel} → ${endLabel}`;
  }

  return startLabel || endLabel || 'Flexible';
}

function formatSkills(skills: ProfileSkill[]): string {
  const labels = skills.map((skill) => skill.title).filter(Boolean);
  return labels.length ? labels.join(', ') : 'No skills';
}

function toTableRows(positions: PositionSummary[]): PositionRow[] {
  return positions.map((position) => ({
    title: position.title,
    detailsPath: `/positions/${position.uuid}`,
    status: position.status,
    budget: formatBudget(position),
    schedule: formatSchedule(position),
    skills: formatSkills(position.skills ?? []),
  }));
}

function Positions() {
  const navigate = useNavigate();
  const { account, accessToken } = useAuth();
  const cachedPositions = account?.uuid
    ? readCachedAccountPositions(account.uuid)
    : readCachedPositions();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [locationPlaceId, setLocationPlaceId] = useState<string | null>(null);
  const [locationCoordinates, setLocationCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [budgetMinInput, setBudgetMinInput] = useState('');
  const [budgetMaxInput, setBudgetMaxInput] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState<PositionBudgetCurrency>(DEFAULT_CURRENCY_CODE);
  const [startsAtInput, setStartsAtInput] = useState('');
  const [endsAtInput, setEndsAtInput] = useState('');
  const [skills, setSkills] = useState<ProfileSkill[]>([]);
  const [categories, setCategories] = useState<ProfileCategory[]>([]);

  const [positions, setPositions] = useState<PositionSummary[]>(() => cachedPositions ?? []);
  const [isLoading, setIsLoading] = useState(() => cachedPositions === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (submitErrorMessage) {
      showSuiErrorSnackbar(submitErrorMessage);
    }
  }, [submitErrorMessage]);

  const loadAccountPositions = async (
    accountUuid: string,
    token?: string
  ): Promise<PositionSummary[]> => fetchAccountPositions(accountUuid, token);

  const reloadActiveAccountPositions = async (accountUuid: string) => {
    const accountPositions = await loadAccountPositions(accountUuid, accessToken ?? undefined);
    setPositions(accountPositions);
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadPositions() {
      const cachedData = account?.uuid
        ? readCachedAccountPositions(account.uuid)
        : readCachedPositions();

      if (!isCancelled && cachedData !== undefined) {
        setPositions(cachedData);
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');
      setSubmitErrorMessage('');

      try {
        if (account?.uuid) {
          const accountPositions = await loadAccountPositions(
            account.uuid,
            accessToken ?? undefined
          );

          if (!isCancelled) {
            setPositions(accountPositions);
          }

          return;
        }

        const allPositions = await fetchPositions(accessToken ?? undefined);
        if (!isCancelled) {
          setPositions(allPositions);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Unable to load positions. Please try again.'
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadPositions();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, account?.uuid]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setLocation('');
    setLocationPlaceId(null);
    setLocationCoordinates(null);
    setBudgetMinInput('');
    setBudgetMaxInput('');
    setBudgetCurrency(DEFAULT_CURRENCY_CODE);
    setStartsAtInput('');
    setEndsAtInput('');
    setSkills([]);
    setCategories([]);
  };

  const handleOpenCreateDialog = () => {
    resetForm();
    setSubmitErrorMessage('');
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (isSubmitting) {
      return;
    }

    setIsCreateDialogOpen(false);
  };

  const handleStartsAtInputChange = (nextValue: string) => {
    setStartsAtInput(nextValue);
    setEndsAtInput((currentEndsAtInput) =>
      normalizeEndDateTimeForStartChange(nextValue, currentEndsAtInput)
    );
  };

  const handleCreatePosition = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedLocation = location.trim();

    if (!account?.uuid) {
      setSubmitErrorMessage('No account found. Sign in again and try once more.');
      return;
    }

    if (normalizedTitle.length < 2) {
      setSubmitErrorMessage('Position title must be at least 2 characters.');
      return;
    }

    if (normalizedDescription.length > 2000) {
      setSubmitErrorMessage('Description must be less than or equal to 2000 characters.');
      return;
    }

    if (normalizedLocation.length > 128) {
      setSubmitErrorMessage('Location must be less than or equal to 128 characters.');
      return;
    }

    const budgetMinValue = budgetMinInput.trim().length
      ? Number(budgetMinInput)
      : null;
    const budgetMaxValue = budgetMaxInput.trim().length
      ? Number(budgetMaxInput)
      : null;

    if (budgetMinValue !== null && (!Number.isFinite(budgetMinValue) || budgetMinValue < 0)) {
      setSubmitErrorMessage('Minimum budget must be a number greater than or equal to 0.');
      return;
    }

    if (budgetMaxValue !== null && (!Number.isFinite(budgetMaxValue) || budgetMaxValue < 0)) {
      setSubmitErrorMessage('Maximum budget must be a number greater than or equal to 0.');
      return;
    }

    if (budgetMinValue !== null && budgetMaxValue !== null && budgetMaxValue < budgetMinValue) {
      setSubmitErrorMessage('Maximum budget must be greater than or equal to minimum budget.');
      return;
    }

    const hasStartInput = startsAtInput.trim().length > 0;
    const hasEndInput = endsAtInput.trim().length > 0;

    if (hasStartInput !== hasEndInput) {
      setSubmitErrorMessage('Start and end times must be provided together.');
      return;
    }

    let startsAtIso: string | null = null;
    let endsAtIso: string | null = null;

    if (hasStartInput && hasEndInput) {
      const startsAtDate = new Date(startsAtInput);
      const endsAtDate = new Date(endsAtInput);

      if (!Number.isFinite(startsAtDate.getTime())) {
        setSubmitErrorMessage('Start time must be a valid date and time.');
        return;
      }

      if (!Number.isFinite(endsAtDate.getTime())) {
        setSubmitErrorMessage('End time must be a valid date and time.');
        return;
      }

      if (endsAtDate.getTime() <= startsAtDate.getTime()) {
        setSubmitErrorMessage('End time must be after start time.');
        return;
      }

      startsAtIso = startsAtDate.toISOString();
      endsAtIso = endsAtDate.toISOString();
    }

    setSubmitErrorMessage('');
    setIsSubmitting(true);

    try {
      await createPositionRequest(
        {
          accountUuid: account.uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
          location: normalizedLocation
            ? {
                placeId: locationPlaceId,
                label: normalizedLocation,
                coordinates: locationCoordinates,
              }
            : null,
          budgetMin: budgetMinValue ?? undefined,
          budgetMax: budgetMaxValue ?? undefined,
          budgetCurrency,
          startsAt: startsAtIso ?? undefined,
          endsAt: endsAtIso ?? undefined,
          skills: skills.length ? skills : undefined,
          categories: categories.length ? categories : undefined,
        },
        accessToken ?? undefined
      );

      resetForm();
      setIsCreateDialogOpen(false);

      if (account?.uuid) {
        await reloadActiveAccountPositions(account.uuid);
      } else {
        const allPositions = await fetchPositions(accessToken ?? undefined);
        setPositions(allPositions);
      }
    } catch (error) {
      setSubmitErrorMessage(
        error instanceof Error ? error.message : 'Unable to create position. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableData = useMemo(
    () => ({
      columns: [
        { Header: 'position', accessor: 'title', width: '24%' },
        { Header: 'status', accessor: 'status', width: '14%' },
        { Header: 'budget', accessor: 'budget', width: '18%' },
        { Header: 'schedule', accessor: 'schedule', width: '20%' },
        { Header: 'skills', accessor: 'skills' },
      ],
      rows: toTableRows(positions),
    }),
    [positions]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  gap={2}
                  mb={!account?.uuid ? 2 : 0}
                >
                  <SoftBox lineHeight={1}>
                    <SoftTypography variant="h5" fontWeight="medium">
                      Positions
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      Positions in your account, including event-linked and independent.
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    onClick={handleOpenCreateDialog}
                    disabled={!account?.uuid}
                  >
                    Create position
                  </SoftButton>
                </SoftBox>
                {!account?.uuid ? (
                  <SoftTypography variant="button" color="text">
                    Account not found. Sign in again.
                  </SoftTypography>
                ) : null}
              </SoftBox>
              {isLoading ? (
                <SoftBox px={3} pb={3}>
                  <SoftTypography variant="button" color="text">
                    Loading positions...
                  </SoftTypography>
                </SoftBox>
              ) : tableData.rows.length === 0 ? (
                <SoftBox px={3} pb={3}>
                  <SoftTypography variant="button" color="text">
                    No positions found yet.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <DataTable
                  table={tableData}
                  canSearch
                  onRowClick={(row: PositionRow) => navigate(row.detailsPath)}
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />

      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create position</DialogTitle>
        <SoftBox component="form" onSubmit={handleCreatePosition}>
          <DialogContent>
            <Stack spacing={2}>
              <SoftInput
                placeholder="Position title"
                value={title}
                onChange={(inputEvent) => setTitle(inputEvent.target.value)}
              />
              <SoftInput
                placeholder="Description (optional)"
                multiline
                rows={3}
                value={description}
                onChange={(inputEvent) => setDescription(inputEvent.target.value)}
              />
              <GooglePlacesAutocomplete
                placeholder="Location (optional)"
                value={location}
                onChange={setLocation}
                onPlaceSelect={(place) => {
                  setLocationPlaceId(place?.placeId ?? null);
                  setLocationCoordinates(place?.coordinates ?? null);
                }}
                disabled={isSubmitting}
                restrictToEstablishments
              />
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Budget currency
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={budgetCurrency}
                  onChange={(inputEvent) =>
                    setBudgetCurrency(inputEvent.target.value as PositionBudgetCurrency)
                  }
                >
                  {CURRENCY_OPTIONS.map((currencyOption) => (
                    <MenuItem key={currencyOption.value} value={currencyOption.value}>
                      {currencyOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SoftBox display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                <SoftInput
                  type="number"
                  placeholder="Minimum budget (optional)"
                  value={budgetMinInput}
                  onChange={(inputEvent) => setBudgetMinInput(inputEvent.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                />
                <SoftInput
                  type="number"
                  placeholder="Maximum budget (optional)"
                  value={budgetMaxInput}
                  onChange={(inputEvent) => setBudgetMaxInput(inputEvent.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </SoftBox>
              <SoftBox display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                <SoftInput
                  type="datetime-local"
                  value={startsAtInput}
                  onChange={(inputEvent) =>
                    handleStartsAtInputChange(inputEvent.target.value)
                  }
                />
                <SoftInput
                  type="datetime-local"
                  value={endsAtInput}
                  onChange={(inputEvent) => setEndsAtInput(inputEvent.target.value)}
                />
              </SoftBox>
              <SoftBox
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
              >
                <CategorySelect
                  accessToken={accessToken ?? null}
                  value={categories}
                  onChange={setCategories}
                  disabled={isSubmitting}
                />
                <SkillsAutocomplete
                  accessToken={accessToken ?? null}
                  value={skills}
                  onChange={setSkills}
                  disabled={isSubmitting}
                />
              </SoftBox>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleCloseCreateDialog}
              disabled={isSubmitting}
            >
              Cancel
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default Positions;
